import type { GetReplacementOptionsWire, ReplacementOptionWire } from "@/services/device-options/device-options.mapper";

export const SIM_LATENCY_MS = 300;
/** Mirrors the upstream eligibility/catalog latency ceiling so the loading state is honestly exercised. */
export const SLOW_LATENCY_MS = 6500;

export const SCENARIO_IDS = {
  bothPlatforms: "both-platforms",
  singleModel: "single-model",
  noLayouts: "no-layouts",
  empty: "empty",
  failure: "failure",
  slow: "slow",
} as const;

export type ScenarioId = (typeof SCENARIO_IDS)[keyof typeof SCENARIO_IDS];

export const SCENARIO_LABELS: Record<ScenarioId, string> = {
  [SCENARIO_IDS.bothPlatforms]: "Both platforms",
  [SCENARIO_IDS.singleModel]: "One model per platform",
  [SCENARIO_IDS.noLayouts]: "No keyboard layouts",
  [SCENARIO_IDS.empty]: "No options",
  [SCENARIO_IDS.failure]: "Load failure",
  [SCENARIO_IDS.slow]: "Slow response",
};

export interface DeviceOptionsScenario {
  id: ScenarioId;
  latencyMs: number;
  shouldFail: boolean;
  wire: GetReplacementOptionsWire;
}

const KEYBOARD_LAYOUTS = ["US English", "UK English", "US International"];

const option = (
  id: string,
  make: string,
  model: string,
  os: "Mac" | "Windows",
  specs: Record<string, string>
): ReplacementOptionWire => ({ id, make, model, specs: { os, ...specs } });

const macPro = option("sim-mac-pro", "Acme", "Notebook Pro 16", "Mac", { processor: "12-core", ram: "36 GB", storage: "1 TB" });
const macAir = option("sim-mac-air", "Acme", "Notebook Air 13", "Mac", { processor: "10-core", ram: "16 GB", storage: "512 GB" });
const winPro = option("sim-win-pro", "Acme", "Portable X1 16", "Windows", { processor: "8-core", ram: "32 GB", storage: "1 TB" });
const winSlim = option("sim-win-slim", "Acme", "Portable X1 14", "Windows", { processor: "6-core", ram: "16 GB", storage: "512 GB" });

const bothPlatformsWire: GetReplacementOptionsWire = {
  options: [macPro, macAir, winPro, winSlim],
  keyboardLayouts: KEYBOARD_LAYOUTS,
};

const scenario = (
  id: ScenarioId,
  wire: GetReplacementOptionsWire,
  overrides: Partial<Omit<DeviceOptionsScenario, "id" | "wire">> = {}
): DeviceOptionsScenario => ({
  id,
  wire,
  latencyMs: SIM_LATENCY_MS,
  shouldFail: false,
  ...overrides,
});

const SCENARIOS: Record<ScenarioId, DeviceOptionsScenario> = {
  [SCENARIO_IDS.bothPlatforms]: scenario(SCENARIO_IDS.bothPlatforms, bothPlatformsWire),
  [SCENARIO_IDS.singleModel]: scenario(SCENARIO_IDS.singleModel, {
    options: [macPro, winPro],
    keyboardLayouts: KEYBOARD_LAYOUTS,
  }),
  [SCENARIO_IDS.noLayouts]: scenario(SCENARIO_IDS.noLayouts, {
    options: [macPro, winSlim],
    keyboardLayouts: [],
  }),
  [SCENARIO_IDS.empty]: scenario(SCENARIO_IDS.empty, { options: [], keyboardLayouts: KEYBOARD_LAYOUTS }),
  [SCENARIO_IDS.failure]: scenario(SCENARIO_IDS.failure, bothPlatformsWire, { shouldFail: true }),
  [SCENARIO_IDS.slow]: scenario(SCENARIO_IDS.slow, bothPlatformsWire, { latencyMs: SLOW_LATENCY_MS }),
};

const isScenarioId = (value: string): value is ScenarioId =>
  Object.values(SCENARIO_IDS).includes(value as ScenarioId);

export const resolveScenario = (id: string): DeviceOptionsScenario =>
  isScenarioId(id) ? SCENARIOS[id] : SCENARIOS[SCENARIO_IDS.bothPlatforms];
